/**
 * The membership-resolved seam for "the current user's startup profile row".
 *
 * Ownership lives in `team_members`, NOT `startup_profiles.user_id` — that
 * column is NULL for every create-path founder (see startup-company.tsx), so
 * the historical `.from('startup_profiles').eq('user_id', authUid)` lookups
 * silently returned nothing for them: the profile never hydrated on login, the
 * data-room connection never persisted, essentials were lost. Everything that
 * needs the caller's own startup now goes through `resolveMyStartup` (which
 * resolves via team_members and falls back to the legacy user_id owner only
 * when no membership row exists yet) and keys by primary `id`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/supabase/database.types';
import { resolveMyStartup } from './use-my-startup';

/**
 * Update the caller's own startup profile, resolving the row via the membership
 * seam and asserting rows-affected. A PostgREST update matching zero rows
 * returns `error: null`, so the error check alone cannot prove the write
 * landed — the count guard is what turns a silent miss into a surfaced error.
 */
export async function updateMyStartupProfile(
  client: SupabaseClient<Database>,
  update: Record<string, unknown>,
): Promise<void> {
  const { startupId } = await resolveMyStartup(client);
  if (!startupId) {
    throw new Error('No startup resolved for the current user — cannot save.');
  }

  // Some columns (e.g. raising_status) aren't in the generated types yet; cast
  // at the query boundary the same way resolveMyStartup does.
  const db = client as any;
  const { error, count } = await db
    .from('startup_profiles')
    .update(update, { count: 'exact' })
    .eq('id', startupId);

  if (error) throw error;
  if (!count) {
    throw new Error(`Update matched no rows for startup ${startupId} — nothing was saved.`);
  }
}

/**
 * Load the caller's own startup profile row (full row), or null if they own
 * none. Resolves via the membership seam, then reads by primary key — the RLS
 * SELECT policy (`is_active_startup_team_member`) authorizes the read.
 */
export async function loadMyStartupProfileRow(
  client: SupabaseClient<Database>,
): Promise<Database['public']['Tables']['startup_profiles']['Row'] | null> {
  const { startupId } = await resolveMyStartup(client);
  if (!startupId) return null;

  const db = client as any;
  const { data, error } = await db
    .from('startup_profiles')
    .select('*')
    .eq('id', startupId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}
